import {buildConversationContentUrl} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {
  HttpNetworkError,
  HttpResponseError,
  HttpTimeoutError,
} from '../../../libs/shared/core/src/services/http/httpError';
import {ApiConversationContentDataGroup} from '../../../src/models/api/dataGroupModel';
import {createLock} from '../../../src/services/locks/lock';
import {ThunkAsyncAction} from '../../../src/store/appThunk';
import {getCompany} from '../../../src/store/data/company/companySelectors';
import {computeConversationContentFetchOptions} from '../../../src/store/panels/viewer/viewerPanelHelpers';
import {offlineConversationContents} from './offlineConversationContents';
import {OfflineError} from './offlineError';
import {fetchThenCacheApiAsync} from './offlineFetch';

/*
 * Constants.
 */

const prefetchConversationLock = createLock();

/*
 * Action creators.
 */

export function prefetchConversationAsyncAction(conversationId: number): ThunkAsyncAction<boolean> {
  return async (_dispatch, getState) =>
    // Avoid both P3 prefetchers racing to prefetch new conversations.
    prefetchConversationLock.lockAsync(async () => {
      const state = getState();
      const company = getCompany(state);
      const options = computeConversationContentFetchOptions(state, conversationId);
      if (!options) {
        return false;
      }

      const {contentUpdatedAt, cacheTimestamp} = options;

      // If the cache is full and our timestamp is older than the oldest entry, don't bother prefetching.
      const canAdd = await offlineConversationContents.canAddTimestampAsync(cacheTimestamp);
      if (!canAdd) {
        return false;
      }

      // If the cache is already up-to-date, no need for prefetching.
      const cacheResult = await offlineConversationContents.readIfLatestAsync(
        conversationId,
        () => contentUpdatedAt,
      );
      if (cacheResult) {
        return true;
      }

      const path = `${company.url}${buildConversationContentUrl(conversationId, {
        shouldExpandMessages: true,
      })}`;

      try {
        await fetchThenCacheApiAsync({
          apiType: ApiConversationContentDataGroup,
          resourceId: conversationId,
          path,
          httpOptions: {headers: {'X-Front-Prefetch': 'timeline-prefetcher'}},
          cacheTimestamp,
        });
      } catch (error) {
        if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
          throw new OfflineError(error);
        }

        if (error instanceof HttpResponseError && error.status < 500) {
          return false;
        }

        throw error;
      }

      return true;
    });
}
